import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import getFooterLinks from '@pcid/utils/get-footer-links';
import { AutoFormattedMessage } from '@pcid/string-utils';
import Link from '@pcid/link';
import LanguageToggle from '../language-toggle';
import { withLocale } from '../locale-provider';
import dynamic from 'next/dynamic';
import { localeCodes } from '@pcid/locale-utils';
import { useIntl } from 'react-intl';

const { Footer: SimpleFooter } = Object.fromEntries(
	['Footer'].map((component) => [
		component,
		dynamic(
			() => import('@helios-ui-web/component-library').then((mod) => mod[component]),
			{ ssr: false }
		),
	])
);

const Footer = ({
	locale,
	setLocale,
	className,
	links,
	isCustomerMigrated = false,
}) => {
	const intl = useIntl();
	const footerLinks = links || getFooterLinks(locale);
	const currentYear = new Date().getFullYear();

	const isEnglish = locale === localeCodes.EN;
	const targetLangCode = isEnglish ? localeCodes.FR : localeCodes.EN;

	const handleLangToggleClick = () => {
		setLocale(targetLangCode);
	};

	useEffect(() => {
		const handleClick = (e) => {
			const anchor = e.target.closest('a');
			if (!anchor) return;

			if (anchor.textContent?.trim() === intl.formatMessage({ id: 'layout.footer.language' })) {
				e.preventDefault();
				handleLangToggleClick();
			}
		};

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [isEnglish]);

	const middleLinks = [
		{
			text: intl.formatMessage({ id: 'layout.footer.language' }),
			url: '#',
		},
		{
			text: intl.formatMessage({ id: 'layout.footer.link.contact' }),
			url: `https://lclcallcenters.my.site.com/pcid/s/contact-us?language=${isEnglish ? 'en_US' : 'fr'}`,
		},
		{
			text: intl.formatMessage({ id: 'layout.footer.link.terms' }),
			url: 'https://accounts.pcid.ca/terms',
		},
		{
			text: intl.formatMessage({ id: 'layout.footer.link.privacy' }),
			url: `https://www.loblaw.ca/${isEnglish ? 'en' : 'fr'}/privacy-policy/`,
		},
	];

	return (
		<>
			{!isCustomerMigrated ? (
				<footer className={classNames('footer', className)}>
					<div className="footer__section">
						<ul className="footer__help-and-support-link-list">
							{Object.keys(footerLinks).map(
								(key) =>
									footerLinks[key] && (
										<li className="footer__help-and-support-link" key={key}>
											<Link
												to={footerLinks[key]}
												target="_blank"
												rel="noopener noreferrer"
											>
												<AutoFormattedMessage id={`layout.footer.link.${key}`} />
											</Link>
										</li>
									)
							)}
						</ul>
						<div className="footer__copyright">
							<AutoFormattedMessage id="layout.footer.allRights" values={{ year: currentYear }}
							/>
						</div>
						<LanguageToggle
							className="footer__language-toggle"
							changeLocale={setLocale}
							locale={locale}
						/>
					</div>
				</footer>
			) : (
				<SimpleFooter
					className="max-w-full"
					disclaimer={{
						className: 'h-11 text-xs',
						aria: {
							Label: 'footer text',
						},
						text: [intl.formatMessage(
							{ id: 'layout.footer.allRights' },
							{ year: currentYear }
						)],
					}}
					middle={{
						className: 'border-t max-w-full flex-row h-16 sm:pl-0',
						aria: {
							Label: 'footer middle navigation',
						},
						links: middleLinks,
					}}
					showTop={false}
				/>
			)}
		</>
	);
};

Footer.propTypes = {
	locale: PropTypes.string,
	setLocale: PropTypes.func.isRequired,
	className: PropTypes.string,
	links: PropTypes.object,
};

export default withLocale(Footer);
